import React from 'react';
import { Award, Users, Target, Heart, CheckCircle, Shield } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from "@/components/ui/Button";
import { Link } from "react-router-dom";

export const AboutPage: React.FC = () => {
  const BASE_URL = import.meta.env.BASE_URL;
  return (
    <div className="min-h-screen">
      {/* Page Hero */}
      <section className="py-16 bg-primary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-title font-bold text-neutral-900 mb-4">About Navjeevan Blood Center</h1>
          <p className="text-body-lg text-neutral-600 max-w-3xl mx-auto">
            Dedicated to providing safe, reliable blood products and exceptional service to our community for over 15 years.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <h2 className="text-subtitle font-bold text-neutral-900 mb-6">Our Story</h2>
              <p className="text-body mb-6 text-neutral-700">
                Established on 6th August 2009, Navjeevan Blood Center began with a simple yet profound mission: to ensure that every person in our community has access to safe blood when they need it most. What started as a small community initiative has grown into one of the region's most trusted blood banking organisations.
              </p>
              <p className="text-body mb-6 text-neutral-700">
                Over the years, we have built strong relationships with local hospitals, healthcare facilities, and community organisations. Our commitment to excellence in blood safety, combined with our compassionate approach to donor care, has made us a vital part of the healthcare ecosystem.
              </p>
              <p className="text-body mb-8 text-neutral-700">
                Today, we serve over 25 partner hospitals and have collected more than 50,000 units of blood, helping to save over 150,000 lives. But our work is far from over – we continue to expand our reach and improve our services to meet the evolving needs of our community.
              </p>
              <Button>
                Our Mission & Vision
              </Button>
            </div>
            <div className="lg:col-span-5">
              <img 
                src={`${BASE_URL}images/medical_theme_1_0.png`} 
                alt="Medical laboratory professionals" 
                className="rounded-lg shadow-lg w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Card className="text-center">
              <CardContent>
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Target className="h-8 w-8 text-primary-600" />
                </div>
                <CardTitle>Our Mission</CardTitle>
                <p className="text-body text-neutral-600 mt-4">
                  To provide safe, adequate, and readily available blood products to meet the transfusion needs of our community while maintaining the highest standards of quality, safety, and service.
                </p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent>
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Heart className="h-8 w-8 text-primary-600" />
                </div>
                <CardTitle>Our Vision</CardTitle>
                <p className="text-body text-neutral-600 mt-4">
                  To be the leading blood center in our region, recognized for excellence in blood safety, innovative donor care, and unwavering commitment to saving lives through blood donation.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
{/* 
     
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-title font-bold text-neutral-900 mb-4">Our Impact</h2>
            <p className="text-body-lg text-neutral-600">Measurable results that save lives</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card hover className="text-center">
              <CardContent>
                <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="h-8 w-8 text-white" />
                </div>
                <div className="text-4xl font-bold text-neutral-900 mb-2">50,000+</div>
                <div className="text-body font-semibold text-neutral-700 mb-2">Total Units Collected</div>
                <div className="text-small text-neutral-600">
                  Blood units collected from dedicated donors over the years
                </div>
              </CardContent>
            </Card>
            <Card hover className="text-center">
              <CardContent>
                <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-8 w-8 text-white" />
                </div>
                <div className="text-4xl font-bold text-neutral-900 mb-2">150,000+</div>
                <div className="text-body font-semibold text-neutral-700 mb-2">Lives Impacted</div>
                <div className="text-small text-neutral-600">
                  Patients whose lives have been touched through blood transfusions
                </div>
              </CardContent>
            </Card>
            <Card hover className="text-center">
              <CardContent>
                <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="h-8 w-8 text-white" />
                </div>
                <div className="text-4xl font-bold text-neutral-900 mb-2">25+</div>
                <div className="text-body font-semibold text-neutral-700 mb-2">Partner Hospitals</div>
                <div className="text-small text-neutral-600">
                  Healthcare facilities that depend on our blood supply
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

       */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">Who We Are</h2>
            <div className="w-20 h-1 bg-primary-600 mx-auto mb-6"></div>
            <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
              Committed to saving lives through blood donation
            </p>
          </div>
          
          <div className="flex flex-col md:flex-row items-start gap-12">
            <div className="w-full md:w-5/12">
              <img 
                src={`${BASE_URL}images/medical_theme_1_3.jpeg`} 
                alt="Our Blood Donation Center"
                className="rounded-lg shadow-xl w-full h-auto object-cover"
              />
            </div>
            <div className="w-full md:w-7/12 space-y-6">
              <p className="text-base md:text-lg text-neutral-700 leading-relaxed">
                Since our founding, we've been committed to building a community where every patient in need has access to safe blood. 
                What started as a small blood donation camp has grown into a full-fledged blood center serving thousands of patients annually.
              </p>
              <p className="text-base md:text-lg text-neutral-700 leading-relaxed">
                Our journey has been guided by the vision of Dr. Priya Sharma, a board-certified pathologist with over 20 years of 
                experience in transfusion medicine. Under her leadership, we've established rigorous safety protocols and built a 
                team of dedicated healthcare professionals who share our commitment to excellence.
              </p>
              <p className="text-base md:text-lg text-neutral-700 leading-relaxed">
                Today, we're proud to be a trusted partner for hospitals across the region, providing life-saving blood products 
                while maintaining the highest standards of safety and care for both donors and recipients.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      {/*<section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-title font-bold text-neutral-900 mb-4">Accreditation and Certification</h2>
            <p className="text-body-lg text-neutral-600">Maintaining the highest standards of quality and safety</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="text-center">
              <CardContent>
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8 text-primary-600" />
                </div>
                <CardTitle>AABB Accredited</CardTitle>
                <p className="text-body text-neutral-600 mt-2">
                  Meeting the most rigorous standards in blood banking
                </p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent>
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-primary-600" />
                </div>
                <CardTitle>FDA Registered</CardTitle>
                <p className="text-body text-neutral-600 mt-2">
                  Compliant with all federal blood safety regulations
                </p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent>
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="h-8 w-8 text-primary-600" />
                </div>
                <CardTitle>ISO Certified</CardTitle>
                <p className="text-body text-neutral-600 mt-2">
                  Quality management system certification
                </p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent>
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="h-8 w-8 text-primary-600" />
                </div>
                <CardTitle>State Licensed</CardTitle>
                <p className="text-body text-neutral-600 mt-2">
                  Fully licensed by state health authorities
                </p>
              </CardContent>
            </Card>
          </div>
          <div className="text-center mt-12">
            <p className="text-body text-neutral-600 mb-6">
              Our certifications demonstrate our commitment to excellence and ensure that we meet or exceed 
              all industry standards for blood safety and quality.
            </p>
            <Button variant="outline">
              View All Certifications
            </Button>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-12 md:py-16 lg:py-20 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">Join Our Mission</h2>
          <p className="text-base sm:text-lg text-red-100 mb-6 sm:mb-8 max-w-3xl mx-auto px-2 sm:px-0">
            Be part of a community that's making a real difference in saving lives.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/donors" state={{ scrollTo: 'register-to-donate' }} className="w-full sm:w-auto">
              <Button size="lg" className="w-full sm:w-auto bg-white text-primary-600 hover:bg-gray-100 transition-all duration-300 transform hover:scale-105">
                Register as Donor
              </Button>
            </Link>
            <Link to="/contact" state={{ scrollTo: 'contact-form' }} className="w-full sm:w-auto">
              <Button variant="outline" size="lg" className="w-full sm:w-auto border-white text-white hover:bg-white hover:text-primary-600 transition-all duration-300 transform hover:scale-105">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
